import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import { FormattedMessage, injectIntl } from 'react-intl';
import classnames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import messages from '../dashboardMessages';
import PeppolActivationStatusChart from './PeppolActivationStatusChart';
import PeppolInvoicingByCustomersChart from './PeppolInvoicingByCustomersChart';
import PeppolDeliveryStatsChart from './PeppolDeliveryStatsChart';
import userStorage from '@/shared/services/userStorage';

const peppolDashboardInitialTabStorageKey = 'PeppolDashboardInitialTab';
const defaultTab = 1;

const getInitialTab = () => {
  const fromStorage = userStorage.getItem(peppolDashboardInitialTabStorageKey);
  if (fromStorage) return fromStorage;
  return defaultTab;
};

const PeppolDashboard = ({ intl, companyId }) => {
  const [rawData, setRawData] = useState(null);
  const [tab, setTab] = useState(getInitialTab);

  useEffect(() => {
    setRawData(null);
    const cancelToken = Axios.CancelToken.source();

    api.base
      .get(`${config.appUrls.api}/api/relation/peppolstatistics/${companyId}`, {
        cancelToken: cancelToken.token,
      })
      .then(({ data }) => {
        setRawData(data || {});
      }, handleHttpError(intl));
    return () => {
      if (cancelToken) cancelToken.cancel('Operation canceled due to new request.');
    };
  }, [companyId, intl]);

  const changeTab = tab => {
    userStorage.setItem(peppolDashboardInitialTabStorageKey, tab);
    setTab(tab);
  };

  const loading = !rawData;
  const hasData = !!rawData;

  return (
    <div className="dashboard-chart">
      <div
        className="title-block"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div>
          <FormattedMessage {...messages.PeppolOnboarding} />
        </div>
        <div className="dashboard-tabs" style={{ display: 'flex', gap: 24 }}>
          <span
            role="button"
            tabIndex={0}
            className={classnames('dashboard-tab', { active: tab === 1 })}
            onClick={() => changeTab(1)}
            style={{
              cursor: 'pointer',
              fontWeight: tab === 1 ? 'bold' : 'normal',
              color: tab === 1 ? '#197d65' : 'inherit',
              fontSize: '80%',
            }}
          >
            <FormattedMessage {...messages.ActivationStatus} />
          </span>
          <span
            className="dashboard-tab-separator"
            style={{ alignSelf: 'center', color: '#ccc', margin: '0 8px' }}
          >
            |
          </span>
          <span
            role="button"
            tabIndex={0}
            className={classnames('dashboard-tab', { active: tab === 2 })}
            onClick={() => changeTab(2)}
            style={{
              cursor: 'pointer',
              fontWeight: tab === 2 ? 'bold' : 'normal',
              color: tab === 2 ? '#197d65' : 'inherit',
              fontSize: '80%',
            }}
          >
            <FormattedMessage {...messages.ReadyToSend} />
          </span>
          <span
            className="dashboard-tab-separator"
            style={{ alignSelf: 'center', color: '#ccc', margin: '0 8px' }}
          >
            |
          </span>
          <span
            role="button"
            tabIndex={0}
            className={classnames('dashboard-tab', { active: tab === 3 })}
            onClick={() => changeTab(3)}
            style={{
              cursor: 'pointer',
              fontWeight: tab === 3 ? 'bold' : 'normal',
              color: tab === 3 ? '#197d65' : 'inherit',
              fontSize: '80%',
            }}
          >
            <FormattedMessage {...messages.SentInvoices} />
          </span>
        </div>
      </div>
      <div className={classnames('content-block', { nodata: !loading && !hasData })}>
        {loading && <LoadingAnimation />}
        {!loading && hasData && (
          <div className="chart-container">
            {tab === 1 && <PeppolActivationStatusChart data={rawData} companyId={companyId} />}
            {tab === 2 && <PeppolInvoicingByCustomersChart data={rawData} companyId={companyId} />}
            {tab === 3 && <PeppolDeliveryStatsChart data={rawData} companyId={companyId} />}
          </div>
        )}
      </div>
    </div>
  );
};

PeppolDashboard.propTypes = {
  intl: intlShape.isRequired,
  companyId: PropTypes.string.isRequired,
};

export default injectIntl(PeppolDashboard);
